/**
 * Suggested questions for the empty help chat panel (#3679), as pure functions.
 *
 * The pools are generated at knowledge-pack build time from the page guides'
 * "Common tasks" (`suggestedQuestions.generated.ts`), one per persona. This
 * module picks the user's pool and keeps only the questions whose page the
 * user can open: the same allowed routes the panel sends with every chat turn,
 * so a page hidden from the user (or switched off for the agency) never gets a
 * pill. The questions are fixed text of ours; picking one sends it through the
 * ordinary chat path like typed text. They are never sent to the model as
 * context.
 */
import { SUGGESTED_QUESTIONS } from "./suggestedQuestions.generated";

/** Presets with a pool of their own; anything else (custom, none) uses the agent pool. */
const PRESET_POOLS = new Set([
  "paralegal",
  "finance",
  "finance_accounts",
  "manager",
  "accounts",
  "office_admin",
  "hr",
  // D5-RBP-005 (#4175): the combined HR/Paralegal desk has its own pool.
  "hr_paralegal",
  "canvasser",
]);

/** The pool key for a user: admin, a back-office preset, else agent. */
export function suggestionPersona(
  role: string | null | undefined,
  preset: string | null | undefined,
): string {
  if (role === "admin") return "admin";
  if (preset && PRESET_POOLS.has(preset)) return preset;
  return "agent";
}

/**
 * The persona's questions whose page is in `allowedRoutes`, in rotation order:
 * questions about the current page first, then the rest starting at `offset`
 * (so repeat openings show a different set first).
 */
export function suggestedQuestions(
  persona: string,
  allowedRoutes: readonly string[],
  currentRoute: string,
  offset = 0,
): string[] {
  const allowed = new Set(allowedRoutes);
  const pool = (SUGGESTED_QUESTIONS[persona] ?? SUGGESTED_QUESTIONS.agent ?? []).filter(
    ([, route]) => allowed.has(route),
  );
  const here = pool.filter(([, route]) => route === currentRoute).map(([q]) => q);
  const rest = pool.filter(([, route]) => route !== currentRoute).map(([q]) => q);
  const start = rest.length ? ((offset % rest.length) + rest.length) % rest.length : 0;
  return [...here, ...rest.slice(start), ...rest.slice(0, start)];
}
